import React, { useState } from 'react';

interface Task {
  id: string;
  number: number;
  title: string;
  description: string;
  requirements: Array<{
    text: string;
    subitems?: string[];
  }>;
  verification: string[];
}

const tasks: Task[] = [
  {
    id: 'task-1',
    number: 1,
    title: 'Web Server Package Installation',
    description: 'Deploy nginx web server software using the system package manager and verify installation.',
    requirements: [
      { text: 'Update package lists with apt-get update' },
      { text: 'Install nginx using Ubuntu package manager (apt-get install -y nginx)' },
      { text: 'Verify installation by checking nginx version (nginx -v)' },
      { text: 'Ensure nginx binary is available in PATH' },
      { text: 'Document installation steps and results' }
    ],
    verification: [
      'nginx package is installed and available',
      'Version information can be queried successfully',
      'Package manager completed without errors',
      'Commands run without interactive prompts'
    ]
  },
  {
    id: 'task-2',
    number: 2,
    title: 'Service Configuration and Management',
    description: 'Start and enable the nginx service for production operation with automatic startup.',
    requirements: [
      { text: 'Start nginx service immediately (systemctl start nginx)' },
      { text: 'Enable nginx to start on boot (systemctl enable nginx)' },
      { text: 'Verify service status shows active (running)' },
      { text: 'Confirm service maintains active status after reload' },
      { text: 'Record status output for verification' }
    ],
    verification: [
      'Service is active (running)',
      'Service is enabled for automatic startup',
      'systemctl status reports no errors',
      'Service responds to management commands'
    ]
  },
  {
    id: 'task-3',
    number: 3,
    title: 'Network Port Configuration Verification',
    description: 'Confirm nginx is properly configured to accept HTTP requests on standard web port 80.',
    requirements: [
      { text: 'Verify nginx is listening on port 80 (ss -tlnp | grep :80)' },
      { text: 'Confirm TCP protocol binding is active' },
      { text: 'Test basic connectivity with curl http://localhost' },
      { text: 'Ensure no other service conflicts on port 80' }
    ],
    verification: [
      'Port 80 shows nginx process listening',
      'TCP connections accepted on port 80',
      'Basic HTTP request returns server response',
      'No port conflicts detected'
    ]
  },
  {
    id: 'task-4',
    number: 4,
    title: 'Custom Web Content Deployment',
    description: 'Create and deploy a custom index.html that prominently displays "do-labs" with proper HTML5 structure.',
    requirements: [
      { text: 'Create custom HTML page in /var/www/html/index.html' },
      { text: 'Page must include the text "do-labs" prominently' },
      { text: 'Use valid HTML5 structure with <!DOCTYPE html>' },
      { text: 'Add basic styling (inline or separate) to enhance visibility' },
      { text: 'Replace any default nginx content with your custom page' }
    ],
    verification: [
      'Custom index.html exists in /var/www/html/',
      'File contains the text "do-labs"',
      'HTML5 structure is present with DOCTYPE',
      'File readable by web server process'
    ]
  },
  {
    id: 'task-5',
    number: 5,
    title: 'HTTP Response Validation',
    description: 'Verify server responses and confirm custom content delivery over HTTP.',
    requirements: [
      { text: 'Request headers with curl -I http://localhost and confirm 200 OK' },
      { text: 'Fetch response body and confirm it includes "do-labs"' },
      { text: 'Ensure no error conditions occur during request' },
      { text: 'Validate content matches the deployed HTML' }
    ],
    verification: [
      'HTTP requests return 200 OK status',
      'Response body contains "do-labs"',
      'No server errors in access/error logs',
      'Content matches deployed file'
    ]
  }
];

export default function NginxSetupInstructions() {
  const [currentTaskIndex, setCurrentTaskIndex] = useState(0);
  const [completedChecks, setCompletedChecks] = useState<Set<string>>(new Set());
  const [completedTasks, setCompletedTasks] = useState<Set<number>>(new Set());

  const handleTaskClick = (index: number) => {
    setCurrentTaskIndex(index);
  };

  const handleNextTask = () => {
    if (currentTaskIndex < tasks.length - 1) {
      setCurrentTaskIndex(currentTaskIndex + 1);
    } else {
      setCurrentTaskIndex(0);
    }
  };

  const handlePreviousTask = () => {
    if (currentTaskIndex > 0) {
      setCurrentTaskIndex(currentTaskIndex - 1);
    }
  };

  const toggleVerification = (taskId: string, checkIndex: number) => {
    const checkId = `${taskId}-check-${checkIndex}`;
    const newChecks = new Set(completedChecks);

    if (newChecks.has(checkId)) {
      newChecks.delete(checkId);
    } else {
      newChecks.add(checkId);
    }

    setCompletedChecks(newChecks);

    const currentTask = tasks[currentTaskIndex];
    const allChecksCompleted = currentTask.verification.every(
      (_, idx) => newChecks.has(`${currentTask.id}-check-${idx}`)
    );

    if (allChecksCompleted && !completedTasks.has(currentTaskIndex)) {
      const newCompletedTasks = new Set(completedTasks);
      newCompletedTasks.add(currentTaskIndex);
      setCompletedTasks(newCompletedTasks);
    }
  };

  const currentTask = tasks[currentTaskIndex];
  const progressPercentage = ((currentTaskIndex + 1) / tasks.length) * 100;

  return (
    <div className="bg-[#232937] rounded-xl overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#10b981] to-[#059669] px-8 py-6">
        <h2 className="text-2xl font-semibold text-[#1a1f2e]">Instructions</h2>
      </div>

      {/* Content Area - Increased to 1800px for less scrolling */}
      <div className="flex h-[1800px]">
        {/* Sidebar Navigation */}
        <div className="w-[280px] bg-[#1a1f2e] border-r border-[#2d3748] overflow-y-auto">
          {tasks.map((task, index) => (
            <div
              key={task.id}
              onClick={() => handleTaskClick(index)}
              className={`flex items-center px-5 py-4 cursor-pointer border-b border-[#2d3748] hover:bg-[#2d3748] transition-all relative ${
                index === currentTaskIndex ? 'bg-[#2d3748] border-l-[3px] border-l-[#10b981] pl-[17px]' : ''
              }`}
            >
              <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-semibold mr-3 flex-shrink-0 ${
                completedTasks.has(index) ? 'bg-[#059669]' : 'bg-[#10b981]'
              } text-[#1a1f2e]`}>
                {task.number}
              </div>
              <div className={`text-sm ${index === currentTaskIndex ? 'text-[#e2e8f0] font-medium' : 'text-[#cbd5e1]'}`}>
                {task.title}
              </div>
              {completedTasks.has(index) && (
                <span className="absolute right-5 text-[#10b981] font-bold text-sm">✓</span>
              )}
            </div>
          ))}
        </div>

        {/* Main Content */}
        <div className="flex-1 p-8 bg-[#1e293b] overflow-y-auto">
          <h3 className="text-[22px] font-semibold text-[#e2e8f0] mb-4">
            Task {currentTask.number}: {currentTask.title}
          </h3>
          <p className="text-[#94a3b8] mb-8 leading-[1.6] text-[15px]">
            {currentTask.description}
          </p>

          {/* Requirements Section */}
          <div className="mb-8">
            <h4 className="text-[#10b981] text-xs uppercase tracking-[1px] mb-4 font-semibold flex items-center">
              <span className="w-5 h-[2px] bg-[#10b981] mr-[10px]"></span>
              What You Need To Do
            </h4>

            {currentTask.requirements.map((requirement, reqIndex) => (
              <div key={reqIndex} className="bg-[#232937] border border-[#2d3748] rounded-lg p-4 mb-3 hover:border-[#374151] hover:shadow-md transition-all">
                <div className="text-[#e2e8f0] text-sm leading-[1.8]">
                  <span className="font-medium">{reqIndex + 1}. {requirement.text}</span>
                  {requirement.subitems && (
                    <ul className="mt-2 ml-5 space-y-1">
                      {requirement.subitems.map((item, idx) => (
                        <li key={idx} className="flex items-start">
                          <span className="text-[#10b981] mr-2 mt-0.5">•</span>
                          <span className="text-[#cbd5e1]">{item}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Verification Section */}
          <div className="bg-[#232937] border-2 border-dashed border-[#475569] rounded-lg p-5 mt-6">
            <div className="flex items-center text-[#10b981] font-semibold mb-4 text-base">
              <span className="w-5 h-5 bg-[#10b981] text-[#1a1f2e] rounded-full flex items-center justify-center text-xs mr-[10px]">
                ✓
              </span>
              Verification
            </div>

            {currentTask.verification.map((check, checkIndex) => (
              <div
                key={checkIndex}
                onClick={() => toggleVerification(currentTask.id, checkIndex)}
                className="flex items-center p-[10px_12px] bg-[#1a1f2e] rounded-md mb-2 cursor-pointer hover:bg-[#1e293b] hover:translate-x-[3px] transition-all last:mb-0"
              >
                <div className={`w-[18px] h-[18px] border-2 border-[#10b981] rounded flex items-center justify-center mr-3 flex-shrink-0 transition-all ${
                  completedChecks.has(`${currentTask.id}-check-${checkIndex}`) ? 'bg-[#10b981]' : ''
                }`}>
                  {completedChecks.has(`${currentTask.id}-check-${checkIndex}`) && (
                    <span className="text-[#1a1f2e] text-xs font-bold">✓</span>
                  )}
                </div>
                <span className="text-[#cbd5e1] text-sm">{check}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Progress Footer */}
      <div className="flex items-center justify-between px-6 py-4 bg-[#1a1f2e] border-t border-[#2d3748]">
        <div className="text-[13px] text-[#94a3b8]">
          Progress: Task {currentTaskIndex + 1} of {tasks.length}
        </div>

        <div className="w-[200px] h-[6px] bg-[#2d3748] rounded-[3px] overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-[#10b981] to-[#34d399] transition-all duration-500 rounded-[3px]"
            style={{ width: `${progressPercentage}%` }}
          />
        </div>

        <div className="flex gap-[10px]">
          <button
            onClick={handlePreviousTask}
            disabled={currentTaskIndex === 0}
            className="px-4 py-2 bg-[#2d3748] border border-[#374151] text-[#cbd5e1] rounded-md text-[13px] cursor-pointer transition-all hover:bg-[#374151] hover:text-[#e2e8f0] disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Previous
          </button>
          <button
            onClick={handleNextTask}
            className="px-4 py-2 bg-[#10b981] border border-[#10b981] text-[#1a1f2e] font-semibold rounded-md text-[13px] cursor-pointer transition-all hover:bg-[#059669] hover:border-[#059669]"
          >
            {currentTaskIndex === tasks.length - 1 ? 'Return to Task 1' : 'Next Task'}
          </button>
        </div>
      </div>
    </div>
  );
}

